// menu-close-signal.ts — PSTUDIO-1433

export interface MenuCloseSignalInput {
  /** The latched nonce this menu opened at, or `null` while it is closed. */
  latchedNonce: number | null;
  /** Whether the menu is currently open (any state other than `closed`). */
  isOpen: boolean;
  /** The store's current close-signal nonce. */
  nonce: number;
}

export interface MenuCloseSignalResult {
  /** The latch to carry into the next evaluation. */
  latchedNonce: number | null;
  /** Whether this evaluation should close the menu. */
  shouldClose: boolean;
}

/**
 * Decides whether a context menu should close in response to the store's
 * close-signal nonce.
 *
 * The nonce is MONOTONIC and never resets, so "it is non-zero" cannot mean "a
 * signal arrived for me": after any earlier scroll has bumped it, a menu that
 * subscribes only while open would see the value jump from 0 to N the instant
 * it OPENS, read that as a signal, and close itself immediately — the menu
 * would flash and vanish on every open for the rest of the session. Latching
 * the value the menu opened at makes the comparison say what it means: close
 * only when the nonce moves while this menu is open.
 *
 * Extracted so both call sites — the cell menu and the row menu — share one
 * copy, and so the flash-on-reopen bug is pinned by a test that needs no DOM.
 */
export function resolveMenuCloseSignal({
  latchedNonce,
  isOpen,
  nonce,
}: MenuCloseSignalInput): MenuCloseSignalResult {
  // Closed: drop the latch so the next open re-arms against a fresh value.
  if (!isOpen) return { latchedNonce: null, shouldClose: false };
  // Just opened: remember where the counter stood; this is not a signal.
  if (latchedNonce === null) return { latchedNonce: nonce, shouldClose: false };
  if (nonce === latchedNonce) return { latchedNonce, shouldClose: false };
  return { latchedNonce: nonce, shouldClose: true };
}
